import React from 'react';
import { Card, Table, Tag, Descriptions, Space, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { formsApi } from '../../api/forms';

const { Text } = Typography;

export const FormSubmissions: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);

    // Fetch form definition to get labels
    const { data: formResult } = useQuery({
        queryKey: ['adminForm', id],
        queryFn: () => formsApi.getAdminFormById(id!),
        enabled: !!id
    });

    const formDefinition = formResult?.data;

    const { data: submissions, isLoading } = useQuery({
        queryKey: ['formSubmissions', id, page, pageSize],
        queryFn: () => formsApi.getSubmissions({ pageNumber: page, pageSize: pageSize, formDefinitionId: id }),
        enabled: !!id
    });

    const columns = [
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (date: string) => new Date(date).toLocaleString()
        },
        {
            title: 'Data',
            dataIndex: 'formData',
            key: 'formData',
            render: (data: string) => {
                try {
                    const parsed = JSON.parse(data);
                    
                    // Map keys to labels from form definition
                    const items = Object.entries(parsed).map(([key, value]) => {
                        const field = formDefinition?.fields?.find((f: any) => f.name === key);
                        const label = field?.label || key;
                        
                        let displayValue = String(value);
                        if (typeof value === 'boolean') displayValue = value ? 'Yes' : 'No';
                        if (Array.isArray(value)) displayValue = value.join(', ');

                        return {
                            key: key,
                            label: <Text strong>{label}</Text>,
                            children: displayValue
                        };
                    });

                    return (
                        <Descriptions 
                            bordered 
                            column={1} 
                            size="small" 
                            items={items}
                            className="striped-descriptions"
                            style={{ maxWidth: '400px' }}
                        />
                    );
                } catch {
                    return <Text type="danger">{data}</Text>;
                }
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => <Tag color="blue">{status}</Tag>
        },
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 120
        }
    ];

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card title={`${formDefinition?.name || 'Form'} Submissions`}>
                <Table 
                    dataSource={submissions?.data || []} 
                    columns={columns} 
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: submissions?.totalRecords || 0,
                        onChange: (p, s) => {
                            setPage(p);
                            setPageSize(s);
                        },
                        showSizeChanger: true
                    }}
                    rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                />
            </Card>
        </Space>
    );
};
